/**
 * PATH: packages/bills/src/components/BillIdentitySection.tsx
 * FILE_ID: BILLS-PKG-CMP-IDENTITY-013
 * PURPOSE: "Bill" section of the data panel — bill number, reference, and the
 *          bill-date / due-date pair, plus the "use supplier-term prediction"
 *          shortcut for the due date.
 *
 * WHY: Groups the invoice-identity inputs away from BillDataPanel's render.
 *      Draft state lives in the parent; this is a props-in view. Extracted
 *      from BillDataPanel.tsx (P4-1 split) — no behaviour change.
 */

import { Input } from '@quotech/ui';
import type { BillDetail } from '../types';
import { Field, Section } from './BillFieldPrimitives';
import type { SetBillField } from './bill-panel-field';

/**
 * Render an ISO yyyy-mm-dd date as "Thursday, 26 March 2026". Parsed
 * manually (not via `new Date(iso)`) so the value is treated as a local
 * calendar date and never shifts a day across timezones.
 */
export function formatHumanDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const [, year, month, day] = m;
  const d = new Date(Number(year), Number(month) - 1, Number(day));
  if (Number.isNaN(d.getTime())) return null;
  const weekday = d.toLocaleDateString(undefined, { weekday: 'long' });
  const monthName = d.toLocaleDateString(undefined, { month: 'long' });
  return `${weekday}, ${d.getDate()} ${monthName} ${d.getFullYear()}`;
}

function DateCaption({ iso }: { iso: string | null | undefined }) {
  const text = formatHumanDate(iso);
  if (!text) return null;
  return (
    <span className="mt-1 block text-[11px] leading-tight text-muted-foreground">
      {text}
    </span>
  );
}

interface BillIdentitySectionProps {
  merged: Partial<BillDetail>;
  predictedDueDate: string | null;
  setField: SetBillField;
}

export function BillIdentitySection({
  merged,
  predictedDueDate,
  setField,
}: BillIdentitySectionProps) {
  return (
    <Section title="Bill">
      <Field label="Bill #">
        <Input
          data-bill-field="invoice_number"
          value={merged.invoice_number ?? ''}
          onChange={(e) => setField('invoice_number', e.target.value)}
          placeholder="e.g. INV-1234"
        />
      </Field>
      <Field label="Reference">
        <Input
          data-bill-field="reference"
          value={merged.reference ?? ''}
          onChange={(e) => setField('reference', e.target.value)}
        />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Bill date">
          <Input
            data-bill-field="invoice_date"
            type="date"
            value={merged.invoice_date ?? ''}
            onChange={(e) => setField('invoice_date', e.target.value || null)}
          />
          <DateCaption iso={merged.invoice_date} />
        </Field>
        <Field label="Due date">
          <Input
            data-bill-field="due_date"
            type="date"
            value={merged.due_date ?? ''}
            onChange={(e) => setField('due_date', e.target.value || null)}
          />
          <DateCaption iso={merged.due_date} />
        </Field>
      </div>
      {predictedDueDate &&
        predictedDueDate !== (merged.due_date ?? '') && (
          <button
            type="button"
            onClick={() => setField('due_date', predictedDueDate)}
            className="text-[11px] text-muted-foreground hover:text-foreground hover:underline underline-offset-2"
            title="Set the due date to the supplier-term prediction"
          >
            Supplier term predicts {formatHumanDate(predictedDueDate) ?? predictedDueDate} — use it
          </button>
        )}
    </Section>
  );
}
